import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, unwrapApiResponse } from '@/utils/api';
import { Plus, Lock, Globe, List, BookOpen, Edit, Trash2, ChevronLeft, X, Loader2 } from 'lucide-react';
import ListCardGrid from '@/components/lists/ListCardGrid';
import { listaLikeToListCard } from '@/components/lists/listCardUtils';
import ListaFormModal from '@/components/lists/ListaFormModal';
import ColecaoObraCard from './ColecaoObraCard';
import { SegmentedTabSwitch } from '@/components/ui/SegmentedTabSwitch';

interface Lista {
    id: number;
    nome: string;
    descricao?: string | null;
    publica: boolean;
    total_obras?: number;
    total_seguidores?: number;
    total_curtidas?: number;
    obras?: {
        id: number;
        nome: string;
        imagem: string | null;
        formato?: { formt_id?: number; id?: number; formt_nome?: string; nome?: string };
    }[];
    usuario?: { id: number; nome: string; nick: string; imagem?: string | null };
    seguindo?: boolean;
    curtiu?: boolean;
    [key: string]: unknown;
}

type ListasTabProps = {
    /** Atualiza o badge de contagem na página Coleção após criar/editar lista ou alterar obras. */
    onListasChanged?: () => void;
};

export default function ListasTab({ onListasChanged }: ListasTabProps) {
    const [minhasListas, setMinhasListas] = useState<Lista[]>([]);
    const [listasSeguidas, setListasSeguidas] = useState<Lista[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState<'minhas' | 'seguindo'>('minhas');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({ nome: '', descricao: '', publica: false });
    const [selectedListaId, setSelectedListaId] = useState<number | null>(null);
    const [selectedLista, setSelectedLista] = useState<Record<string, unknown> | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const normalizeListasPayload = (raw: unknown): Lista[] => {
        if (Array.isArray(raw)) return raw as Lista[];
        if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
            return (raw as { data: Lista[] }).data;
        }
        return [];
    };

    const fetchListas = async () => {
        setLoading(true);
        try {
            const [minhasRes, seguidasRes] = await Promise.all([api.get('listas'), api.get('listas/seguidas')]);
            setMinhasListas(normalizeListasPayload(unwrapApiResponse<unknown>(minhasRes)));
            setListasSeguidas(normalizeListasPayload(unwrapApiResponse<unknown>(seguidasRes)));
        } catch {
            /* mantém estado anterior */
        } finally {
            setLoading(false);
        }
    };

    const fetchListaDetail = async (id: number) => {
        setLoadingDetail(true);
        try {
            const res = await api.get(`listas/${id}`);
            setSelectedLista(unwrapApiResponse<Record<string, unknown>>(res));
        } catch {
            setSelectedListaId(null);
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => { fetchListas(); }, []);
    useEffect(() => { if (selectedListaId) fetchListaDetail(selectedListaId); }, [selectedListaId]);

    useEffect(() => {
        if (!selectedListaId || isModalOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setSelectedListaId(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedListaId, isModalOpen]);

    useEffect(() => {
        if (!selectedListaId) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [selectedListaId]);

    const handleSubmitLista = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.nome.trim()) return;
        setIsSubmitting(true);
        try {
            if (isEditing && selectedListaId) {
                await api.patch(`listas/${selectedListaId}`, formData);
                if (selectedLista) setSelectedLista({ ...selectedLista, ...formData });
            } else { await api.post('listas', formData); }
            setIsModalOpen(false); setFormData({ nome: '', descricao: '', publica: false }); setIsEditing(false);
            await fetchListas();
            onListasChanged?.();
        } catch (error) { alert('Erro ao salvar lista'); } finally { setIsSubmitting(false); }
    };

    const handleRemoveObra = async (obrId: number) => {
        if (!selectedListaId) return;
        try {
            await api.post(`listas/${selectedListaId}/adicionar-remover-obra`, { obra: obrId });
            setSelectedLista((prev: Record<string, unknown> | null) => {
                if (!prev || !Array.isArray(prev.obras)) return prev;
                const obras = (prev.obras as Lista['obras'] | undefined)?.filter((o) => o && o.id !== obrId) ?? [];
                const nextTotal = typeof prev.total_obras === 'number' ? Math.max(0, prev.total_obras - 1) : obras.length;
                return { ...prev, obras, total_obras: nextTotal };
            });
            await fetchListas();
            onListasChanged?.();
        } catch {
            /* falha silenciosa; pode reabrir detalhe para sincronizar */
        }
    };

    const currentListas = activeSubTab === 'minhas' ? minhasListas : listasSeguidas;

    const obraFormatoParaCard = (obra: NonNullable<Lista['obras']>[number]) => {
        const f = obra.formato;
        if (!f || typeof f !== 'object') return undefined;
        const id = Number(f.formt_id ?? f.id);
        const nome = String(f.formt_nome ?? f.nome ?? '').trim();
        if (!nome) return undefined;
        return { id: Number.isFinite(id) ? id : 0, nome };
    };

    return (
        <div className="relative">
            {selectedListaId ? (
                <div
                    className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label={String(selectedLista?.nome ?? 'Lista')}
                >
                    <button
                        type="button"
                        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
                        aria-label="Fechar lista"
                        onClick={() => setSelectedListaId(null)}
                    />
                    <div className="relative flex h-[min(88dvh,920px)] max-h-[96dvh] w-full max-w-6xl min-h-0 flex-col overflow-hidden rounded-t-[1.35rem] border border-white/10 border-b-0 bg-neutral-950 shadow-[0_-20px_60px_-15px_rgba(0,0,0,0.85)] animate-in fade-in slide-in-from-bottom-3 duration-300 sm:h-[min(90dvh,920px)] sm:rounded-2xl sm:border-b sm:shadow-2xl">
                        <div className="h-1 w-full shrink-0 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500/70" />
                        <div className="flex shrink-0 flex-col gap-3 border-b border-white/5 bg-neutral-950/90 px-4 py-3 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
                            <div className="flex min-w-0 items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setSelectedListaId(null)}
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition-all hover:border-orange-500/30 hover:bg-white/[0.07] hover:text-white"
                                    aria-label="Voltar"
                                >
                                    <ChevronLeft size={22} />
                                </button>
                                <div className="min-w-0">
                                    <h2 className="line-clamp-2 text-base font-semibold leading-tight text-white sm:text-lg">
                                        {String(selectedLista?.nome ?? 'Carregando…')}
                                    </h2>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-white/45">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-semibold ${selectedLista?.publica ? 'border-emerald-500/20 text-emerald-400/90' : 'border-white/10 text-white/50'}`}
                                        >
                                            {selectedLista?.publica ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                                            {selectedLista?.publica ? 'Pública' : 'Privada'}
                                        </span>
                                        <span className="tabular-nums">
                                            {(typeof selectedLista?.total_obras === 'number' ? selectedLista.total_obras : (selectedLista?.obras as Lista['obras'])?.length) || 0}{' '}
                                            obras
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center gap-2 pl-[3.25rem] sm:pl-0">
                                <Link
                                    to={`/listas/${selectedListaId}`}
                                    className="inline-flex items-center justify-center rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-white/60 transition-colors hover:border-orange-500/25 hover:bg-white/[0.04] hover:text-white sm:text-sm"
                                >
                                    Ver página da lista
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => setSelectedListaId(null)}
                                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-white/45 transition-colors hover:border-white/20 hover:bg-white/[0.04] hover:text-white sm:hidden"
                                    aria-label="Fechar"
                                >
                                    <X size={18} />
                                </button>
                                {activeSubTab === 'minhas' && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!selectedLista) return;
                                            setFormData({
                                                nome: String(selectedLista.nome ?? ''),
                                                descricao: String(selectedLista.descricao ?? ''),
                                                publica: Boolean(selectedLista.publica),
                                            });
                                            setIsEditing(true);
                                            setIsModalOpen(true);
                                        }}
                                        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-white/90 transition-colors hover:border-orange-500/30 hover:bg-white/[0.04]"
                                    >
                                        <Edit size={14} className="text-orange-400/80" /> Editar
                                    </button>
                                )}
                            </div>
                        </div>
                        {selectedLista?.descricao ? (
                            <p className="line-clamp-2 shrink-0 border-b border-white/5 px-4 py-2.5 text-xs leading-relaxed text-white/40 sm:px-5">
                                {String(selectedLista.descricao)}
                            </p>
                        ) : null}
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-5 sm:py-5">
                            {loadingDetail ? (
                                <div className="flex justify-center py-20">
                                    <Loader2 className="h-9 w-9 animate-spin text-orange-500" />
                                </div>
                            ) : ((selectedLista?.obras as Lista['obras'])?.length ?? 0) === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                                    <BookOpen className="size-10 text-white/15" aria-hidden />
                                    <p className="text-sm font-medium text-white/45">Esta lista ainda não tem obras.</p>
                                    {activeSubTab === 'minhas' ? (
                                        <p className="max-w-xs text-xs text-white/30">Adicione títulos a partir da página de cada obra.</p>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5">
                                    {(selectedLista?.obras as Lista['obras'])!.map((obra) => {
                                        const fmt = obraFormatoParaCard(obra);
                                        return (
                                            <div
                                                key={obra.id}
                                                className="group rounded-xl border border-white/10 bg-white/[0.02] p-2 transition-colors hover:border-orange-500/25"
                                            >
                                                <ColecaoObraCard
                                                    context="status"
                                                    showFooter={false}
                                                    work={{
                                                        id: obra.id,
                                                        nome: obra.nome,
                                                        imagem: obra.imagem ?? undefined,
                                                        formato: fmt,
                                                    }}
                                                    overlayStart={
                                                        activeSubTab === 'minhas' ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleRemoveObra(obra.id);
                                                                }}
                                                                className="rounded-full border border-white/10 bg-black/50 p-2 text-white/60 shadow-md backdrop-blur-md transition-all hover:bg-black/70 hover:text-red-400 md:opacity-0 md:group-hover:opacity-100"
                                                                aria-label="Remover da lista"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        ) : undefined
                                                    }
                                                    overlayEnd={
                                                        fmt ? (
                                                            <span className="rounded border border-white/10 bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/90 backdrop-blur-md">
                                                                {fmt.nome}
                                                            </span>
                                                        ) : undefined
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <SegmentedTabSwitch<'minhas' | 'seguindo'>
                            value={activeSubTab}
                            onChange={setActiveSubTab}
                            ariaLabel="Minhas listas ou seguindo"
                            uppercase={false}
                            className="sm:max-w-md"
                            options={[
                                { value: 'minhas', label: 'Minhas listas' },
                                { value: 'seguindo', label: 'Seguindo' },
                            ]}
                        />
                        {activeSubTab === 'minhas' && (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    setFormData({ nome: '', descricao: '', publica: false });
                                    setIsModalOpen(true);
                                }}
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-500"
                            >
                                <Plus size={16} strokeWidth={2.5} /> Nova lista
                            </button>
                        )}
                    </div>

                    <div>
                        {loading && currentListas.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-white/40">
                                <Loader2 className="mb-3 h-8 w-8 animate-spin text-orange-500" />
                                <p className="text-sm font-medium">Carregando listas…</p>
                            </div>
                        ) : null}
                        {!loading && currentListas.length === 0 ? (
                            <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-16 text-center">
                                <List className="mb-4 h-10 w-10 text-white/20" />
                                <p className="font-semibold text-white/80">
                                    {activeSubTab === 'minhas' ? 'Nenhuma lista ainda' : 'Você não segue nenhuma lista'}
                                </p>
                                <p className="mt-2 max-w-md text-sm text-white/40">
                                    {activeSubTab === 'minhas'
                                        ? 'Crie uma lista na página da obra ou aqui para agrupar títulos.'
                                        : 'Explore a página de listas públicas e siga as que preferir.'}
                                </p>
                            </div>
                        ) : null}
                        {!loading && currentListas.length > 0 ? (
                            <ListCardGrid
                                lists={currentListas.map((lista) => listaLikeToListCard(lista))}
                                onOpenList={(id) => setSelectedListaId(Number(id))}
                            />
                        ) : null}
                    </div>
                </div>
            )}
            <ListaFormModal
                open={isModalOpen}
                mode={isEditing ? 'edit' : 'create'}
                formData={formData}
                onFormDataChange={setFormData}
                isSubmitting={isSubmitting}
                onClose={() => !isSubmitting && setIsModalOpen(false)}
                onSubmit={handleSubmitLista}
            />
        </div>
    );
}
